import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User } from '../../types';
import { TOKEN_KEY } from '../../services/api/client';
import { loginRequest, meRequest, registerRequest, RegisterPayload, RegisterResult } from '../../services/api/auth';
import { captureLocationOnce } from '../location/captureLocation';
import { enablePush, pushPermission } from '../push/registerPush';

interface AuthContextValue {
  // undefined = still checking session, null = not authenticated
  user: User | null | undefined;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (payload: RegisterPayload) => Promise<RegisterResult>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return;
    }
    meRequest()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      });
  }, []);

  const login = async (email: string, password: string) => {
    const res = await loginRequest(email, password);
    localStorage.setItem(TOKEN_KEY, res.access_token);
    setUser(res.user);
    return res.user;
  };

  const register = async (payload: RegisterPayload) => {
    return await registerRequest(payload);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const u = await meRequest();
      setUser(u);
    } catch {
      /* keep current state */
    }
  };

  const isAdmin = !!user && user.roles.includes('admin');

  // Capture member location once per session (GPS w/ permission → IP fallback).
  useEffect(() => {
    if (user) captureLocationOnce();
  }, [user]);

  // If push permission already granted, keep this device subscribed (silent).
  useEffect(() => {
    if (user && pushPermission() === 'granted') enablePush(false);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, isAdmin, login, register, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
